import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { MatchPreviewCard } from '@/components/MatchPreviewCard';
import { RoundedMatchesScroller } from '@/components/RoundedMatchesScroller';
import { isCompleted, isLive as isLiveStatus } from '@/constants/matchStatus';
import { MainSection } from '@/components/layout/MainSection';
import { useTournamentDetailContext } from '@/layouts/TournamentDetailLayout/context';
import type { Match } from '@/layouts/TournamentDetailLayout/context';
import { injectKnockoutBracket } from '@/data/injectKnockoutBracket';
import styles from '../TournamentDetailPage.module.scss';

const MatchesTab: React.FC = () => {
  const { tournament, tournamentId, isArchived } = useTournamentDetailContext();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation(['tournament', 'common']);

  // Fetch matches data.
  const {
    data: matchesData,
    isLoading: matchesLoading,
    error: matchesError,
  } = useQuery({
    queryKey: ['tournament-matches', tournamentId],
    queryFn: async () => {
      const response = await apiClient.getTournamentMatches(tournamentId, undefined, true);
      return response.data;
    },
    enabled: !!tournamentId && isAuthenticated,
  });

  const matches: Match[] = useMemo(() => {
    // Inject the World Cup 2026 knockout-bracket placeholder template here at
    // the data-layer level. The injector is a pure function and short-circuits
    // to the input array unchanged for any tournament that isn't the FIFA
    // World Cup 2026 (league external id 1, season 2026). For matching
    // tournaments, it adds synthetic placeholder matches for any knockout
    // stage the API hasn't yet published. Downstream `buildStageMatchdays`
    // groups them into round pills exactly like real matches.
    const raw: Match[] = matchesData?.data || [];
    return injectKnockoutBracket(raw, tournament);
  }, [matchesData, tournament]);

  const renderCard = useCallback(
    (match: Match) => (
      <MatchPreviewCard
        matchId={match.id}
        tournamentId={tournamentId}
        team1Name={match.team1}
        team2Name={match.team2}
        team1Placeholder={match.team1_placeholder}
        team2Placeholder={match.team2_placeholder}
        team1LogoUrl={match.team1_logo_url}
        team2LogoUrl={match.team2_logo_url}
        score1={isCompleted(match.status) || isLiveStatus(match.status) ? match.score_team1 : null}
        score2={isCompleted(match.status) || isLiveStatus(match.status) ? match.score_team2 : null}
        status={match.status}
        time={format(new Date(match.match_date), 'HH:mm')}
        venue={match.venue}
        elapsedMinutes={match.elapsed_minutes}
        elapsedExtraMinutes={match.elapsed_extra_minutes}
        elapsedUpdatedAt={match.elapsed_updated_at}
        team1WinCoefficient={match.team1_win_coefficient}
        drawCoefficient={match.draw_coefficient}
        team2WinCoefficient={match.team2_win_coefficient}
        userPrediction={match.user_prediction}
      />
    ),
    [tournamentId],
  );

  const scheduleLink = (
    <Link to={`/tournaments/${tournamentId}/schedule`} className="text-button">
      {t('tournament:schedule')}
    </Link>
  );

  // Body content selection.
  // Loading branch follows the project's "tab components return null while
  // loading" policy; the scroller handles its own empty state and the gap
  // between matches arriving and the initial round being detected.
  let body: React.ReactNode;
  if (matchesError) {
    body = (
      <div className={styles.error}>
        <div className={styles.errorIcon}>!</div>
        <p className={styles.errorText}>{t('tournament:errorLoadingMatches')}</p>
      </div>
    );
  } else if (matchesLoading) {
    body = null;
  } else {
    body = <RoundedMatchesScroller matches={matches} renderCard={renderCard} />;
  }

  return (
    <MainSection
      fill
      hideTitleOnDesktop
      subtitleHideOnMobile
      title={tournament?.name ?? t('tournament:tournamentMatches')}
      subtitle={t('tournament:matches')}
      logoUrl={tournament?.league?.logo_url || tournament?.leagueLogo}
      logoAlt={tournament?.league?.name || tournament?.leagueName}
      headerRight={scheduleLink}
    >
      {isArchived && (
        <div className={styles.archivedNotice} role="status">
          <p className={styles.archivedNoticeTitle}>
            {t('tournament:tournamentArchivedNotice')}
          </p>
        </div>
      )}
      {body}
    </MainSection>
  );
};

export default MatchesTab;
